import type { InputHTMLAttributes, ReactNode, Ref, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { cn } from '@/lib/cn';

const control =
  'w-full rounded-[var(--radius-control)] border bg-surface px-3 text-sm text-ink ' +
  'placeholder:text-ink-subtle transition-colors focus:border-secondary focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:bg-canvas-soft';

export function Field({ label, error, hint, required, children }: {
  label: string; error?: string; hint?: string; required?: boolean; children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-ink-muted">
        {label}
        {required && <span className="text-danger"> *</span>}
      </span>
      {children}
      {error
        ? <span role="alert" className="text-xs text-danger">{error}</span>
        : hint && <span className="text-xs text-ink-subtle">{hint}</span>}
    </label>
  );
}

export function TextInput({ error, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { error?: boolean }) {
  return (
    <input
      {...rest}
      aria-invalid={error || undefined}
      className={cn(control, 'h-10', error ? 'border-danger' : 'border-line', className)}
    />
  );
}

export function TextArea({ error, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  error?: boolean; ref?: Ref<HTMLTextAreaElement>;
}) {
  return (
    <textarea
      {...rest}
      aria-invalid={error || undefined}
      className={cn(control, 'min-h-24 py-2', error ? 'border-danger' : 'border-line', className)}
    />
  );
}

export function Select({ error, className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { error?: boolean }) {
  return (
    <select
      {...rest}
      aria-invalid={error || undefined}
      className={cn(control, 'h-10', error ? 'border-danger' : 'border-line', className)}
    >
      {children}
    </select>
  );
}

/** Labelled form field with a generated id, for inputs that need one. */
export function useFieldId(prefix: string) {
  return `${prefix}-${useId()}`;
}

/**
 * The native colour swatch, given a consistent size and border. Three places
 * used a bare `<input type="color">` and each rendered at a different size.
 */
export function ColorInput({ className, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return (
    <input
      {...rest}
      type="color"
      className={cn(
        'size-8 shrink-0 cursor-pointer rounded-[var(--radius-control)] border border-line',
        'bg-surface p-0.5 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    />
  );
}
